import { loadChairishSimilarItems } from '@/redux/modules/chairishSimilarItems/chairishSimilarItems.actions';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { setIsZeroResults, submitSearch } from '@/redux/modules/search/search.actions';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { fetchSimilarItemsGroups, getSimilarItemsGroupsItemIds } from '@/redux/modules/similarItemsGroups';
import { AppDispatch, AppGetState } from '@/redux/store';
import { DataLoaderParams } from '@/types/DataLoaderParams';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { SearchPageName } from '@/types/search/searchQuery';
import { parseQueryParams } from '@/utils/queryParams';
import { buildSearchQuery } from '@/utils/search/searchQuery';

export type LoadZeroResultsDataParams = {
    browseFacetOptionIds: Parameters<typeof buildSearchQuery>[0]['browseFacetOptionIds'];
    /** The keyword search-party corrected the typed one to, when it did. */
    correctedSearchTerm: string | undefined;
    dispatch: AppDispatch;
    getState: AppGetState;
    pageName: SearchPageName;
    pageSize: number;
    pathname: string;
    queryParams: ReturnType<typeof parseQueryParams>;
    routerState?: DataLoaderParams['routerState'];
    shouldPreloadSoldItems: boolean;
};

/**
 * Loads what the "no upcoming results, but we've sold N before" page shows: the sold lots, then the
 * similar items built from the first lot. Shared by the route loader and the excluded-house refetch so
 * both build the page the same way.
 */
export const loadZeroResultsData = async ({
    browseFacetOptionIds,
    correctedSearchTerm,
    dispatch,
    getState,
    pageName,
    pageSize,
    pathname,
    queryParams,
    routerState,
    shouldPreloadSoldItems,
}: LoadZeroResultsDataParams) => {
    const { soldItemIds = [] } = getSearchUiState(getState());

    if (shouldPreloadSoldItems) {
        await dispatch(setIsZeroResults(true));
        await dispatch(
            submitSearch({
                pageName,
                routerState,
                searchQuery: buildSearchQuery({
                    browseFacetOptionIds,
                    defaultSortOrder: SearchSortAndDirection.TimeNewest,
                    pathname,
                    queryParams: {
                        ...queryParams,
                        // A corrected response's sold count is for the corrected keyword, so its lots must be too.
                        ...(correctedSearchTerm && { keyword: correctedSearchTerm }),
                        status: 'archive',
                    },
                    searchPageSize: pageSize,
                    similarItems: true,
                }),
                skipAggs: true,
            })
        );
    }

    await dispatch(
        fetchItemSummariesIfNeeded({
            identifier: 'SEARCH-PAGE-SOLD',
            itemIds: soldItemIds,
            similarItems: true,
        })
    );

    const { itemIds } = getSearchUiState(getState());
    if (itemIds?.length > 0) {
        const itemId = itemIds[0];

        await Promise.all([dispatch(fetchSimilarItemsGroups(itemId)), dispatch(loadChairishSimilarItems({ itemId }))]);

        return dispatch(
            fetchItemSummariesIfNeeded({
                identifier: 'SEARCH-PAGE-SIMILAR',
                itemIds: getSimilarItemsGroupsItemIds(getState(), itemId),
            })
        );
    }
};
